import React from "react";
import PropTypes from "prop-types";
import IPropTypes from "react-immutable-proptypes";
import { List } from "immutable";

import { Button, ErrorContent, Icon, SimpleLoader, Typography } from "ui-library/atoms";
import { Box, Flexbox } from "ui-library/layouts";

import i18n from "core/i18n";
import { FileUploadStatus } from "enums";

const FAILED_STATE = [FileUploadStatus.UPLOAD_FAILED, FileUploadStatus.VALIDATION_FAILED];
const REMOVABLE_STATE = [FileUploadStatus.UPLOADED, FileUploadStatus.UPLOAD_FAILED, FileUploadStatus.VALIDATION_FAILED];

export const MultiPartUploadFilesList = ({ onChange, value, meta, size, removeFileCallback, removeFileId }) => {
    const fieldErrorList = meta?.error;
    const onRemoveFile = (removedFile) => {
        if (removeFileCallback && removeFileId && removedFile.get(removeFileId)) {
            removeFileCallback(removedFile.get(removeFileId));
        }
        return onChange(value ? value.filter((file) => file.get("id") !== removedFile.get("id")) : List());
    };

    return (
        <>
            {/* Error message - From redux store */}
            {fieldErrorList?.size > 0 && fieldErrorList.map((error) => <ErrorContent key={error} content={error} />)}

            {/* List of uploaded files */}
            {<CustomFiles files={value} onRemoveFile={onRemoveFile} size={size} />}
        </>
    );
};

const CustomFiles = ({ files, onRemoveFile, size }) => {
    const { t } = i18n.useTranslation();

    return (
        <>
            <Box mt={3}>
                <Typography variant="caption" color="gray">
                    {t("common.uploadedFiles")}
                </Typography>
            </Box>

            {files.map((file) => {
                const isFailedState = FAILED_STATE.includes(file.get("status"));
                const validationErrors = file.get("validationErrors") || i18n.translateDirectly("error.uploadFailed");
                const fileName = isFailedState ? `${file.get("fileName")} (${validationErrors})` : file.get("fileName");

                return (
                    <Flexbox key={file.get("id")} gap={1} alignItems={"center"} style={{ height: "42px" }}>
                        <Icon icon={isFailedState ? "circle-error" : "document-check"} color={isFailedState ? "red" : "success"} />

                        <Box>
                            <Typography fontSize={size === "small" ? 14 : null}>{fileName}</Typography>
                        </Box>

                        {file.get("status") === FileUploadStatus.UPLOADING && <SimpleLoader loading={true} />}

                        {REMOVABLE_STATE.includes(file.get("status")) && (
                            <Button onClick={() => onRemoveFile(file)} variant="text" color="blue" fontSize={size === "small" ? 14 : null}>
                                {t("common.remove")}
                            </Button>
                        )}
                    </Flexbox>
                );
            })}
        </>
    );
};

MultiPartUploadFilesList.propTypes = {
    meta: PropTypes.shape({
        error: IPropTypes.list,
        touched: PropTypes.bool.isRequired,
        asyncValidating: PropTypes.bool,
        dirty: PropTypes.bool,
        valid: PropTypes.bool,
        form: PropTypes.string,
    }).isRequired,
    /** Value with list of uploaded files. */
    value: IPropTypes.listOf(
        IPropTypes.contains({
            id: PropTypes.string.isRequired,
            fileName: PropTypes.string.isRequired,
            size: PropTypes.number,
            fileFormat: PropTypes.string,
            errorMsg: PropTypes.string,
            data: PropTypes.string.isRequired,
        }),
    ),
    size: PropTypes.string,
    removeFileCallback: PropTypes.func,
    removeFileId: PropTypes.string,
};

MultiPartUploadFilesList.defaultProps = {
    value: List(),
    size: "medium",
    removeFileCallback: null,
    removeFileId: null,
};
